import type { TFunction, TOptions } from "i18next";
import type { JSX, ParentComponent, VoidComponent } from "solid-js";
import * as htmlparser2 from "htmlparser2";
import { ElementType } from "htmlparser2";
import { defaultsDeep, isNil } from "lodash-es";
import type { TranslationKey } from "../state/i18n";
import { useTranslation } from "../state/i18n";
import logger from "../utils/logger";
import type { NodeWithChildren } from "domhandler";

type TranslationProps = {
  key: TranslationKey;
  options?: TOptions;
  // oxlint-disable-next-line @typescript-eslint/no-explicit-any -- We don't know the type, but `unknown` will mean a lot of typing boilerplate
  components: Record<string, ParentComponent<Record<string, any>>>;
};
export const Translation: VoidComponent<TranslationProps> = (props) => {
  const { t } = useTranslation();

  return <>{translateJSX(t, props)}</>;
};

export const translateJSX = (t: TFunction, props: TranslationProps) => {
  const translation = t(
    props.key,
    defaultsDeep(props.options, {
      interpolation: {
        // We need to escape interpolation values, otherwise these will be filtered out by the rendering below
        escapeValue: true,
      },
    }),
  ) as string;

  try {
    const doc = htmlparser2.parseDocument(translation, {
      recognizeSelfClosing: true,
      lowerCaseTags: false,
      lowerCaseAttributeNames: false,
    });

    const render = (node: NodeWithChildren): JSX.Element =>
      node.children
        .map((child) => {
          if (child.type === ElementType.Text) {
            return child.nodeValue;
          } else if (child.type === ElementType.Tag) {
            const component = props.components[child.name];
            if (isNil(component)) {
              logger.error("Translation contains an unknown component", {
                extra: {
                  translationKey: props.key,
                  component: child.name,
                },
              });
              return;
            }

            return component(
              child.attributes.reduce((acc, cur) => ({ ...acc, [cur.name]: cur.value }), { children: render(child) }),
            );
          }
        })
        .filter(Boolean);

    return render(doc);
  } catch (e) {
    logger.error("Failed to translate", e, { extra: { translationKey: props.key } });
  }
};
